import { Button, Form, Modal } from "antd";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import {
  createSearchParams,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import arrowBack from "../../assets/images/arrow-back.svg";
import leftIcon from "../../assets/images/left-arr.svg";
import rightGrayIcon from "../../assets/images/right-arr-gray.svg";
import rightWhiteIcon from "../../assets/images/right-arr-white.svg";
import { Loader } from "../../shared/components/Loader";
import CurrentSteps from "../../shared/layout/Steps";
import AxiosInstance from "../../shared/utils/axios";
import { fetchProtocolTemplateData } from "../protocols/protocolsService";
import { formatTranslation } from "../../shared/utils/translationUtils";
import "../style.less";
import { protocolsSteps, SUBMIT_PROTOCOL_LABEL } from "./constants";
import type {
  createProtocolFormType,
  createProtocolValidationDataType,
} from "./shared/interfaces/product-interfaces";
import "./style.less";
import ProductsComponent from "./step-components/ProductsComponent";
import RulesComponent from "./step-components/RulesComponent";
import SummaryComponent from "./step-components/SummaryComponent";
import TrialTypesComponent from "./step-components/TrialTypesComponent";
import { type ProtocolStepItemType } from "./types";
import {
  getCurrentStepNumber,
  getItems,
  getNextButtonLabel,
  isBiologicalHasValidData,
  isSeedHasValidData,
  nextStep,
  prevStep,
} from "./utils";
import SaveTemplateModal from "./shared/components/SaveTemplateModal";
import { useTrialsRouter } from "../../shared/custom-hooks/useTrialsRouter";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import {
  ProtocolAmplitudeEvent,
  ProtocolTemplatesAmplitudeEvent,
} from "../../shared/utils/amplitudeEvents";
import { useFeatureFlags } from "../../context/LDFlagsContext";

export type nextStepOptions = "draft" | "published" | "configure-trial";
type ProductTypeKey = "seed" | "biological" | "nutrient";

const PRODUCT_TYPE_ORDER: ProductTypeKey[] = ["seed", "biological", "nutrient"];
const PRODUCT_TYPE_BUTTON_LABELS: Record<ProductTypeKey, string> = {
  seed: "Seeds",
  biological: "CP and Biologicals",
  nutrient: "Nutrients",
};

const CreateProtocolContext = createContext<any>(undefined);

export const useCreateProtocolForm = (): any => {
  const context = useContext(CreateProtocolContext);
  if (context === undefined) {
    throw new Error(
      "useCreateProtocolForm must be used within a CreateProtocolProvider"
    );
  }
  return context;
};

const CreateProtocol = (): ReactElement => {
  const { t } = useTranslation();
  const { router: trialsRouter } = useTrialsRouter();
  const { router: protocolsRouter, workspaceId, orgId } = useProtocolsRouter();
  const [openBackModal, setOpenBackModal] = useState(false);
  const { enableProtocolTemplates } = useFeatureFlags();
  const trackProtocolEvents = useTrackingEvent({
    page: ProtocolAmplitudeEvent.PROTOCOLS_PAGE,
    group: ProtocolAmplitudeEvent.PROTOCOLS_GROUP,
    module: ProtocolAmplitudeEvent.PROTOCOLS_GROUP,
  });
  const showBackModal = (): void => {
    setOpenBackModal(true);
  };
  const handleOkBackModal = (): void => {
    protocolsRouter.goToProtocolsList();
  };

  const handleCancelModal = (): void => {
    setOpenBackModal(false);
  };
  const [form] = Form.useForm<createProtocolFormType>();
  const formValues = form.getFieldsValue(true);
  const { step } = useParams();

  const [formValidationData, setFormValidationData] =
    useState<createProtocolValidationDataType | null>(null);

  const [checkValue, setCheckValue] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] =
    useState<boolean>(false);

  const currentStepNumber = getCurrentStepNumber(step ?? "");

  const items = getItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const protocolId = searchParams.get("protocol_id");
  const templateId = searchParams.get("template_id");
  const [templateData, setTemplateData] = useState<any>(null);
  const activeProductTypeFromQuery = searchParams.get("type");
  const protocolTypes =
    (Array.isArray(formValues?.protocolType) && formValues?.protocolType) ||
    formValidationData?.protocolType ||
    [];

  const selectedProductTypes = PRODUCT_TYPE_ORDER.filter((type) =>
    protocolTypes.includes(type)
  );

  const activeProductType = selectedProductTypes.includes(
    activeProductTypeFromQuery as ProductTypeKey
  )
    ? (activeProductTypeFromQuery as ProductTypeKey)
    : selectedProductTypes[0];

  const nextProductType = activeProductType
    ? selectedProductTypes[selectedProductTypes.indexOf(activeProductType) + 1]
    : undefined;

  const previousProductType = activeProductType
    ? selectedProductTypes[selectedProductTypes.indexOf(activeProductType) - 1]
    : undefined;

  const values = Form.useWatch([], form);

  useEffect(() => {
    const fetchTemplateData = async (): Promise<void> => {
      if (!templateId || protocolId) return;
      try {
        const data = await fetchProtocolTemplateData(
          templateId,
          orgId ?? "",
          workspaceId ?? ""
        );
        setTemplateData(data);
        const initialValues: Record<string, any> = {
          protocolType: data.protocol_type,
          protocolName: data.name,
          protocolDescription: data.description,
          protocolCrop: data.crop,
          protocolCountry: data.crop_details?.country,
          protocolCountries: data.crop_details?.countries,
          protocolCropId: data.crop_details?.crop_id,
          plotType: data.plot_type,
          objectives: data.objectives,
          trial_application_method: data.trial_application_method,
          seed_details: data.product_details?.seeds,
          nutrient_details: data.product_details?.nutrients,
          biologicals: data.product_details?.biologicals,
        };
        if (data.trial_application_method === "plot") {
          initialValues.plotMinWidth = data.plot_minimum_size?.width;
          initialValues.plotMinLength = data.plot_minimum_size?.length;
          initialValues.plotRecommendedWidth =
            data.plot_recommended_size?.width;
          initialValues.plotRecommendedLength =
            data.plot_recommended_size?.length;
        } else if (data.trial_application_method === "strip") {
          initialValues.stripMinWidth = data.strip_size?.min_width;
          initialValues.stripMaxWidth = data.strip_size?.max_width;
        }
        form.setFieldsValue(initialValues);
        setTimeout(() => {
          void form.validateFields();
          setFormValidationData((prev: any) => ({
            ...(prev ?? {}),
            protocolType: data.protocol_type,
            // Seed-specific fields (products/measure_unit/density...) are
            // deliberately NOT set here. ProductsSeedComponent owns them: it
            // re-validates persisted products against the current country's
            // catalog before trusting them. Setting them here too raced that
            // validation -- this fetch resolves faster (one request vs. the
            // seed component's fetch-then-validate chain), so it could win
            // and show a stale, unvalidated selection as "complete" for a
            // moment (or longer, if the seed component's chain stalled).
            isNutrientDataValid: !!data.product_details?.nutrients,
            // Immutable snapshot of the ORIGINAL template selections. Used to
            // fully restore crop + seed + bio products if the user navigates
            // the country back to the one the template was created with.
            originalTemplateSnapshot: {
              country: data.crop_details?.country,
              cropName: data.crop,
              cropId: data.crop_details?.crop_id,
              localizedCropIds: data.crop_details?.localized_crop_ids ?? [],
              seedDetails: data.product_details?.seeds ?? null,
              biologicals: data.product_details?.biologicals ?? null,
              // Saving the Rules step nulls out whichever type (plot/strip)
              // isn't currently selected (see handleStepTwo below) -- these
              // are kept here so BasicInfoComponent can restore them if the
              // user later switches back to that type.
              plotMinimumSize: data.plot_minimum_size ?? null,
              plotRecommendedSize: data.plot_recommended_size ?? null,
              stripSize: data.strip_size ?? null,
            },
          }));
        }, 100);
      } catch (error) {
        console.error("Error fetching template data:", error);
      }
    };
    void fetchTemplateData();
  }, [templateId]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const fetchProtocolData = async () => {
      if (protocolId) {
        try {
          const response = await AxiosInstance.get(`/protocol/${protocolId}`, {
            headers: {
              "org-id": orgId,
              "workspace-id": workspaceId ?? "",
            },
          });
          const protocolData = response.data;
          const initialValues = {
            protocolType: protocolData.protocol_type,
            protocolName: protocolData.name,
            seed_details: protocolData.product_details?.seeds,
            nutrient_details: protocolData.product_details?.nutrients,
            biologicals: protocolData.product_details?.biologicals,
            trial_application_method: protocolData.trial_application_method,
          };
          form.setFieldsValue(initialValues);
          setTimeout(() => {
            void form.validateFields();
            setFormValidationData((prev: any) => ({
              ...(prev ?? {}),
              protocolType: protocolData.protocol_type,
              // Seed-specific fields (products/measure_unit/density...) are
              // deliberately NOT set here -- see the matching comment in the
              // template-fetch effect above. ProductsSeedComponent is the
              // sole owner; it re-validates persisted products against the
              // current country's catalog before trusting them.
              isNutrientDataValid: !!protocolData.product_details?.nutrients,
              complete: protocolData.complete,
              status: protocolData.status,
            }));
          }, 100);
        } catch (error) {
          console.error("Error fetching protocol:", error);
        }
      }
    };
    void fetchProtocolData();
  }, [protocolId]);

  useEffect(() => {
    if (currentStepNumber === 1) {
      if (
        formValidationData?.protocolType &&
        formValidationData?.protocolType.length > 0
      ) {
        setCheckValue(true);
      } else {
        setCheckValue(false);
      }
    }
    // `formValidationData.protocolType` is populated by a delayed
    // (setTimeout) setFormValidationData call when loading from a template
    // or existing protocol, which lands after this effect's other deps have
    // already settled -- it must be in the dependency array or this never
    // re-evaluates once that update arrives. Depend only on `protocolType`
    // (the sole `formValidationData` field this effect reads) so unrelated
    // validation updates (products, revalidation flags, etc.) don't trigger
    // needless re-runs / extra renders.
  }, [values, formValues, formValidationData?.protocolType, currentStepNumber]);

  useEffect(() => {
    if (currentStepNumber === 2) {
      // A country change may still be revalidating whether the selected crop
      // exists in the new country (crop list + seed catalog calls in
      // BasicInfoComponent). Block advancing until that resolves so the user
      // can't proceed with a crop that's about to be cleared.
      if (formValidationData?.cropRevalidationInProgress) {
        setCheckValue(false);
        return;
      }
      form
        .validateFields({ validateOnly: true })
        .then(() => {
          if (formValues.objectives?.length > 0) setCheckValue(true);
          else {
            setCheckValue(false);
          }
        })
        .catch((err: { outOfDate?: boolean }) => {
          // rc-field-form rejects an OUTDATED validateFields() call (one
          // superseded by a newer call before it settled) with an empty
          // `errorFields` array and `outOfDate: true`, regardless of whether
          // its fields were actually valid. This effect re-fires rapidly
          // while a type switch (Plot/Strip) restores field values, so a
          // slow, stale call can settle AFTER the latest (valid) call already
          // set checkValue(true) -- wrongly stomping it back to false forever
          // since nothing ever re-triggers this effect again. Ignore
          // outdated rejections; only a genuinely current one may disable
          // the button.
          if (err?.outOfDate) return;
          setCheckValue(false);
        });
    }
  }, [values, formValues, formValidationData, currentStepNumber]);

  useEffect(() => {
    if (currentStepNumber === 3) {
      // A country-change revalidation in flight means the current
      // products/treatments may still be the stale, pre-change selection --
      // never treat the type as valid until that revalidation has settled.
      // `??` would stop at the first explicit `false` instead of falling
      // through to the next flag, so `||` (not nullish coalescing) is
      // required here.
      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      const seedRevalidationPending = Boolean(
        formValidationData?.seedRevalidationRequired ||
          formValidationData?.seedRevalidationProcessing ||
          formValidationData?.clearProductsAndCalculations
      );
      const bioRevalidationPending = Boolean(
        formValidationData?.bioRevalidationRequired ||
          formValidationData?.clearProductsAndCalculations
      );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

      const seedsChecks =
        !seedRevalidationPending && isSeedHasValidData(formValidationData);

      const nutrientsChecks = Boolean(formValidationData?.isNutrientDataValid);

      const biologicalsChecks =
        !bioRevalidationPending &&
        Boolean(
          isBiologicalHasValidData(
            formValues?.biologicals ? formValues?.biologicals : {}
          )
        );

      const validityByType: Record<ProductTypeKey, boolean> = {
        seed: Boolean(seedsChecks),
        biological: biologicalsChecks,
        nutrient: nutrientsChecks,
      };

      const allSelectedProductTypesValid = selectedProductTypes.every(
        (type) => validityByType[type]
      );

      let currentTypeValid = false;

      if (activeProductType === "seed") {
        currentTypeValid = validityByType.seed;
      } else if (activeProductType === "biological") {
        currentTypeValid = validityByType.biological;
      } else if (activeProductType === "nutrient") {
        currentTypeValid = validityByType.nutrient;
      }

      setCheckValue(
        nextProductType ? currentTypeValid : allSelectedProductTypesValid
      );
    }
  }, [
    form,
    formValues,
    formValidationData,
    activeProductType,
    selectedProductTypes,
    nextProductType,
    currentStepNumber,
  ]);

  const getPrimaryNextLabel = (): string => {
    if (currentStepNumber === 3 && nextProductType) {
      return PRODUCT_TYPE_BUTTON_LABELS[nextProductType];
    }
    return getNextButtonLabel(currentStepNumber);
  };

  const getPrimaryBackLabel = (): string => {
    if (currentStepNumber === 1) return "Cancel";

    if (currentStepNumber === 3 && previousProductType) {
      return PRODUCT_TYPE_BUTTON_LABELS[previousProductType];
    }

    const previousStep = prevStep(step ?? "");
    return previousStep?.title ?? "Back";
  };

  const handleNextStep = async (
    nextStepOption: nextStepOptions
  ): Promise<void> => {
    if (protocolsSteps[currentStepNumber]) {
      void trackProtocolEvents({
        event: ProtocolAmplitudeEvent.PROTOCOL_STEPS,
        originAction: protocolsSteps[currentStepNumber]?.title,
      });
    }

    const formValues = form.getFieldsValue(true);
    const nextStepData: ProtocolStepItemType = nextStep(step ?? "");

    setIsUpdating(true);
    try {
      if (currentStepNumber === 1) {
        handleStepOne(nextStepData);
      } else if (currentStepNumber === 2) {
        await handleStepTwo(formValues, nextStepData);
      } else if (currentStepNumber === 3 && protocolId !== null) {
        await handleStepThree(formValues, nextStepData);
      } else if (
        step === items[items?.length - 1].path &&
        protocolId !== null
      ) {
        await handleFinalStep(nextStepOption);
      }
    } catch (error) {
      console.error("common.error", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStepOne = (nextStepData: ProtocolStepItemType): void => {
    if (protocolId !== null) {
      const params: Record<string, string> = { protocol_id: protocolId };
      if (templateId) params.template_id = templateId;
      protocolsRouter.goToProtocolsEditPage(
        createSearchParams(params).toString(),
        protocolId,
        nextStepData.path
      );
    } else if (templateId) {
      protocolsRouter.goToProtocolsCreatePage(
        createSearchParams({ template_id: templateId }).toString(),
        nextStepData.path
      );
    } else {
      protocolsRouter.goToProtocolsCreatePage(undefined, nextStepData.path);
    }
  };

  const handleStepTwo = async (
    formValues: any,
    nextStepData: ProtocolStepItemType
  ): Promise<void> => {
    const payload: any = {
      org_id: orgId,
      workspace_id: workspaceId ?? "",
      name: formValues.protocolName,
      description: formValues.protocolDescription,
      crop: formValues.protocolCrop,
      crop_details: {
        countries: formValues.protocolCountries,
        country: formValues.protocolCountry,
        crop_id: formValues.protocolCropId,
      },
      protocol_type: formValues.protocolType,
      plot_type: formValues.plotType,
      objectives: formValues.objectives,
      template_id: templateId ?? null,
      trial_application_method: formValues.trial_application_method,
      ...(formValues.trial_application_method === "plot"
        ? {
            plot_minimum_size: getPlotSize(
              formValues.plotMinWidth,
              formValues.plotMinLength
            ),
            plot_recommended_size: getPlotSize(
              formValues.plotRecommendedWidth,
              formValues.plotRecommendedLength
            ),
            strip_size: null,
          }
        : {
            strip_size: getStripSize(
              formValues.stripMinWidth,
              formValues.stripMaxWidth
            ),
            plot_recommended_size: null,
            plot_minimum_size: null,
          }),
    };
    if (protocolId === null) {
      if (templateId) {
        payload.product_details = {
          seeds: formValues?.seed_details ?? null,
          biologicals: formValues?.biologicals ?? null,
          nutrients: formValues?.nutrient_details ?? null,
        };
      }
      await makeApiCall("/protocol", "post", payload, nextStepData);
    } else {
      await makeApiCall(
        `/protocol/${protocolId}`,
        "patch",
        payload,
        nextStepData
      );
    }
  };

  const handleStepThree = async (
    formValues: any,
    nextStepData: ProtocolStepItemType
  ): Promise<void> => {
    const seedDetails: any = { ...(formValues?.seed_details ?? {}) };

    const densityMode =
      (formValidationData as any)?.globalDensityMode ?? "range";
    const globalOptions = (formValidationData as any)?.globalDensityOptions;

    const products: any[] = Array.isArray(seedDetails?.products)
      ? seedDetails.products
      : [];

    if (densityMode === "range") {
      seedDetails.density_options = null;

      seedDetails.products = products.map((p: any) => ({
        ...p,
        density_options: null,
      }));

      if (protocolTypes.includes("seed") && !seedDetails?.density_range?.min) {
        seedDetails.density_range = null;
      }
    } else if (densityMode === "options") {
      seedDetails.density_range = null;

      seedDetails.products = products.map((p: any) => ({
        ...p,
        density_range: null,
      }));

      if (seedDetails?.global_density_range === true) {
        seedDetails.density_options = Array.isArray(globalOptions)
          ? globalOptions
          : [];

        seedDetails.products = seedDetails.products.map((p: any) => ({
          ...p,
          density_options: Array.isArray(p?.density_options)
            ? p.density_options
            : seedDetails.density_options,
        }));
      }
    }

    const isSeedTypeSelected = protocolTypes.includes("seed");
    const hasSeedProducts =
      Array.isArray(seedDetails?.products) && seedDetails.products.length > 0;

    const normalizedSeedsPayload = isSeedTypeSelected
      ? {
          ...seedDetails,
          products: hasSeedProducts ? seedDetails.products : null,
        }
      : null;

    const payload = {
      product_details: {
        seeds: normalizedSeedsPayload,
        biologicals: formValues?.biologicals,
        nutrients: formValues?.nutrient_details,
      },
    };

    if (protocolId !== null) {
      await makeApiCall(
        `/protocol/${protocolId}`,
        "patch",
        payload,
        nextStepData
      );
    }
  };

  const handleFinalStep = async (
    nextStepOption: nextStepOptions
  ): Promise<void> => {
    const payload =
      nextStepOption === "draft"
        ? { status: "draft" }
        : { status: "published" };
    if (protocolId !== null) {
      await makeApiCall(
        `/protocol/${protocolId}`,
        "patch",
        payload,
        undefined,
        nextStepOption
      );
    }
  };

  const handleError = (messageKey: string, error?: any): void => {
    toast.error(formatTranslation(t(messageKey)));
    if (error) {
      console.error(formatTranslation(t(messageKey)), error);
    } else {
      console.error(formatTranslation(t(messageKey)));
    }
  };

  const makeApiCall = async (
    url: string,
    method: "post" | "patch",
    payload: any,
    nextStepData?: ProtocolStepItemType,
    nextStepOption?: nextStepOptions
  ): Promise<void> => {
    try {
      const config = {
        headers: {
          "org-id": orgId ?? "",
          "workspace-id": workspaceId ?? "",
        },
      };

      const response = await AxiosInstance[method](url, payload, config);
      if (response?.status !== 200 && !response?.data?.id) {
        throw new Error(
          response?.data?.errors?.[0]?.message || "common.error.patch"
        );
      }
      if (nextStepData) {
        const newProtocolId = response?.data?.id ?? protocolId;
        const nextParams: Record<string, string> = {
          protocol_id: newProtocolId as string,
        };
        if (templateId) nextParams.template_id = templateId;
        setSearchParams(nextParams);
        protocolsRouter.goToProtocolsEditPage(
          createSearchParams(nextParams).toString(),
          newProtocolId as string,
          nextStepData.path
        );
      } else if (nextStepOption) {
        nextStepOption === "configure-trial"
          ? trialsRouter.goToTrialsCreatePage()
          : protocolsRouter.goToProtocolsList();
      }
    } catch (error: any) {
      if (error?.response?.data?.errors?.length > 0) {
        handleError("protocol.toastMessage.patch", error);
      } else {
        handleError(
          method === "patch" ? "common.error.patch" : "common.error.post",
          error
        );
      }
    }
  };

  const getPlotSize = (width: number, length: number): any => {
    return isNaN(width ?? NaN) || isNaN(length ?? NaN) ? {} : { width, length };
  };

  const getStripSize = (minWidth: number, maxWidth: number): any => {
    return isNaN(minWidth ?? NaN) || isNaN(maxWidth ?? NaN)
      ? {}
      : { min_width: minWidth, max_width: maxWidth };
  };

  const handlePrevStep = (): void => {
    if (currentStepNumber === 3 && previousProductType) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("type", previousProductType);
      setSearchParams(updatedSearchParams);
      return;
    }

    const pervStepData: ProtocolStepItemType | any = prevStep(step ?? "");
    if (pervStepData !== undefined) {
      // Preserve template_id (and protocol_id) when navigating back so that
      // the Trial Type page still knows it originated from a template and can
      // re-show the "Change Type Alert" on subsequent changes.
      const params: Record<string, string> = {};
      if (protocolId !== null) params.protocol_id = protocolId;
      if (templateId) params.template_id = templateId;

      if (Object.keys(params).length > 0) {
        protocolsRouter.goToProtocolsEditPage(
          createSearchParams(params).toString(),
          protocolId as string,
          pervStepData.path
        );
      } else {
        protocolsRouter.goToProtocolsCreatePage(undefined, pervStepData.path);
      }
    } else {
      setOpenBackModal(true);
    }
  };

  const handleOpenTemplateModal = (): void => {
    void trackProtocolEvents({
      event:
        ProtocolTemplatesAmplitudeEvent.SAVE_AS_TEMPLATE_WORKSPACE_UNIVERSAL,
      originAction: "save-as-template-workspace-universal",
    });
    setIsTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = (): void => {
    setIsTemplateModalOpen(false);
  };

  return (
    <div className="createProtocol-page">
      {isUpdating && <Loader mask message="Saving" />}
      <Modal
        open={openBackModal}
        title={formatTranslation(t("modal.goBackToListHeaderProtocol"))}
        onOk={handleOkBackModal}
        onCancel={handleCancelModal}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Button onClick={handleCancelModal}>
              {formatTranslation(t("modal.cancel"))}
            </Button>
            <Button
              className="ct-bg-red ct-text-white"
              onClick={handleOkBackModal}
            >
              {formatTranslation(t("modal.leavePage"))}
            </Button>
          </>
        )}
      >
        <p>{formatTranslation(t("modal.goBackToList"))}</p>
        <p>{formatTranslation(t("modal.goBackToTrailFooter"))}</p>
      </Modal>
      <SaveTemplateModal
        open={isTemplateModalOpen}
        protocolId={protocolId}
        protocolName={form.getFieldValue("protocolName") ?? ""}
        orgId={orgId ?? ""}
        workspaceId={workspaceId ?? ""}
        onClose={handleCloseTemplateModal}
      />
      <div className="createProtocol-header">
        <div className="breadcrumbs" data-testid="breadcrumbs-div">
          <Button
            className="Left-button-icon"
            onClick={() => {
              showBackModal();
            }}
          >
            <img src={arrowBack} alt="left-icon" />
          </Button>
          <p>
            {formValidationData?.isNewProtocol
              ? formatTranslation(t("createProtocol.title"))
              : formatTranslation(t("createProtocol.titleEdit"))}
          </p>
        </div>
        <div className="stepper-common">
          <CurrentSteps current={currentStepNumber - 1} items={items} />
        </div>
      </div>
      <div className="createProtocol-body">
        <Form
          data-testid="form-basic"
          name="basic"
          form={form}
          style={
            currentStepNumber === 3 || currentStepNumber === 4
              ? { width: "100%" }
              : {}
          }
          layout="vertical"
          initialValues={{
            protocolType: null,
            plotRecommendedWidth: null,
            plotRecommendedLength: null,
            plotMinWidth: null,
            plotMinLength: null,
            stripMinWidth: null,
            stripMaxWidth: null,
          }}
        >
          <ToastContainer stacked />
          <div className="router-outlet">
            <CreateProtocolContext.Provider
              value={{
                form,
                formValues,
                formValidationData,
                setFormValidationData,
              }}
            >
              {step === "trial-type" && (
                <TrialTypesComponent templateData={templateData} />
              )}
              {step === "rules" && <RulesComponent />}
              {step === "products" && <ProductsComponent />}
              {step === "summary" && <SummaryComponent />}
            </CreateProtocolContext.Provider>
          </div>
        </Form>
      </div>
      <div className="bottom-sec-btn">
        <div className="bottom-sec-left-actions">
          <Button
            size="large"
            className="cancel-btn"
            data-testid="cancel-btn"
            onClick={handlePrevStep}
          >
            {<img src={leftIcon} style={{ color: "black" }} alt="left-icon" />}
            {getPrimaryBackLabel()}
          </Button>
          {formValidationData &&
            (formValidationData.status === "published" ||
              formValidationData.complete === true) &&
            currentStepNumber === 4 &&
            protocolId !== null &&
            enableProtocolTemplates && (
              <Button
                size="large"
                className="next-btn draft-btn template-btn"
                onClick={handleOpenTemplateModal}
              >
                {formatTranslation(t("createProtocol.saveAsTemplate"))}
              </Button>
            )}
        </div>

        <div className="bottom-sec-right-actions split-button">
          {currentStepNumber === 4 && protocolId !== null ? (
            <>
              <Button
                size="large"
                className="next-btn draft-btn"
                onClick={() => {
                  void trackProtocolEvents({
                    event: ProtocolAmplitudeEvent.SAVE_AS_DRAFT,
                    originAction: "save-as-draft",
                  });
                  void handleNextStep("draft");
                }}
              >
                {formatTranslation(t("createProtocol.dropdownOption1"))}
              </Button>
              <Button
                size="large"
                className="next-btn publish-btn"
                type="primary"
                onClick={() => {
                  void trackProtocolEvents({
                    event: ProtocolAmplitudeEvent.PUBLISH_PROTOCOL,
                    originAction: "publish-protocol",
                  });
                  void handleNextStep("published");
                }}
              >
                {formatTranslation(t("createProtocol.dropdownOption2"))}
              </Button>
            </>
          ) : (
            <Button
              size="large"
              onClick={() => {
                void (async () => {
                  if (
                    currentStepNumber === 3 &&
                    nextProductType &&
                    checkValue
                  ) {
                    const updatedSearchParams = new URLSearchParams(
                      searchParams
                    );
                    updatedSearchParams.set("type", nextProductType);
                    setSearchParams(updatedSearchParams);
                    return;
                  }
                  await handleNextStep("configure-trial");
                })();
              }}
              className={`next-btn ${
                currentStepNumber === 4 ? "protocol-summary-btn" : ""
              } ${checkValue ? "" : "active"}`}
              htmlType="submit"
              data-testid="next-btn"
              disabled={!checkValue}
              loading={Boolean(formValidationData?.cropRevalidationInProgress)}
            >
              {getPrimaryNextLabel()}
              {getPrimaryNextLabel() !== SUBMIT_PROTOCOL_LABEL && (
                <img
                  src={checkValue ? rightWhiteIcon : rightGrayIcon}
                  alt="left-icon"
                />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProtocol;
